import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Settings, LogOut, MessageCircle, Sparkles, Building2, Banknote } from 'lucide-react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { orderedNavItems } from './data';
import { ROUTES } from '../../config/constants';
import dayjs from 'dayjs';
import { getUserDisplayName, getUserInitials as getInitials } from '../../utils/userUtils';
import { getTokenPayload } from '../../utils/jwtUtils';

const AccordionItem = ({ menu, setSidebarOpen, location }) => {
    const [isOpen, setIsOpen] = useState(false);
    // Check if any accordion item is active, handling query parameters
    const isActive = menu.items?.some(item => {
        const itemPathname = item.path.split('?')[0];
        const itemSearch = item.path.includes('?') ? `?${item.path.split('?')[1]}` : '';
        // Require exact match of both pathname and search (including when both are empty)
        return location.pathname === itemPathname && location.search === itemSearch;
    });
    const contentRef = useRef(null);

    return (
        <div>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={clsx(
                    "w-full flex items-center gap-3 py-3 px-4 text-sm font-medium transition-colors rounded-lg",
                    isActive
                        ? "bg-gray-200 text-black"
                        : "text-gray-500 hover:bg-gray-100 hover:text-black active:bg-gray-100"
                )}
            >
                <menu.icon size={20} className="flex-shrink-0" />
                <span className="flex-1 text-left text-gray-600 break-words">{menu.label}</span>
                <ChevronDown
                    size={16}
                    className={clsx(
                        "transition-transform duration-200 text-gray-500 flex-shrink-0",
                        isOpen && "rotate-180"
                    )}
                />
            </button>
            {isOpen && (
                <div ref={contentRef} className="ml-5 pl-3 border-l border-gray-200 space-y-0.5 py-1">
                    {menu.items?.map((item) => {
                        // Manually check if item is active, handling query parameters
                        const itemPathname = item.path.split('?')[0];
                        const itemSearch = item.path.includes('?') ? `?${item.path.split('?')[1]}` : '';
                        // Require exact match of both pathname and search (including when both are empty)
                        const isItemActive = location.pathname === itemPathname && location.search === itemSearch;
                        
                        return (
                            <NavLink
                                key={item.id}
                                to={item.path}
                                onClick={() => setTimeout(() => setSidebarOpen(false), 150)}
                                className={clsx(
                                    "flex items-center gap-3 py-2.5 px-3 text-sm transition-colors rounded-lg min-h-[44px]",
                                    isItemActive
                                        ? "bg-gray-200 text-black font-medium"
                                        : "text-gray-600 hover:bg-gray-100 hover:text-black active:bg-gray-100"
                                )}
                            >
                                <item.icon size={16} className="text-gray-500 flex-shrink-0" />
                                <span className="truncate text-gray-600">{item.label}</span>
                            </NavLink>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const MobileNavLink = ({ item, location, onNavigate, setSidebarOpen }) => {
    // For paths with query strings, match both pathname and search
    const itemPathname = item.path.split('?')[0];
    const itemSearch = item.path.includes('?') ? `?${item.path.split('?')[1]}` : '';
    // Require exact match of both pathname and search (including when both are empty)
    const isActive = location.pathname === itemPathname && location.search === itemSearch;

    const handleClick = () => {
        if (item.isNeurolawResearch) {
            onNavigate(item.path, true);
        } else {
            onNavigate(item.path, false);
        }
        // Small delay for smooth transition
        setTimeout(() => setSidebarOpen(false), 150);
    };

    return (
        <button
            onClick={handleClick}
            className={clsx(
                "w-full flex items-center gap-3 py-3 px-4 text-sm font-medium text-left transition-colors rounded-lg min-h-[44px]",
                isActive
                    ? "bg-gray-200 text-black"
                    : "text-gray-500 hover:bg-gray-100 hover:text-black active:bg-gray-100"
            )}
        >
            <item.icon size={20} className="flex-shrink-0" />
            <span className="flex-1 text-gray-600 break-words">{item.label}</span>
        </button>
    );
};

const MobileNav = ({ user, actions, location, onNavigate }) => {
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
    const navigate = useNavigate();

    // Redux state for live credits and points
    const reduxCredits = useSelector((state) => state.credits?.value ?? 0);
    const reduxPoints = useSelector((state) => state.points?.value ?? 0);

    // Check if user is org member from JWT
    const isOrgMember = (() => {
        const payload = getTokenPayload();
        if (!payload) return false;
        return payload.isOrgUser === true && payload.accountType !== 'organization';
    })();

    const [showAssistant, setShowAssistant] = useState(() => {
        try {
            const saved = localStorage.getItem('showAssistant');
            return saved !== null ? JSON.parse(saved) : false;
        } catch (e) {
            return false;
        }
    });

    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (isSidebarOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isSidebarOpen]);

    const getUserInitials = () => {
        return getInitials(user);
    };

    return (
        <div className="lg:hidden">
            <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 text-gray-500 hover:text-black hover:bg-gray-100 rounded-lg transition-colors active:bg-gray-200"
                aria-label="Open menu"
            >
                <Menu size={24} />
            </button>

            <div
                onClick={() => setSidebarOpen(false)}
                className={clsx(
                    "fixed inset-0 bg-black/60 backdrop-blur-sm transition-all duration-300",
                    isSidebarOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
                )}
                style={{ zIndex: 99999 }}
            />

            <aside
                className={clsx(
                    "fixed top-0 left-0 h-dvh w-full sm:w-80 max-w-[95vw] bg-gray-50 transition-all duration-300 transform shadow-2xl flex flex-col",
                    isSidebarOpen ? "translate-x-0" : "-translate-x-full"
                )}
                style={{ zIndex: 100000 }}
            >
                {/* Header with Logo and Settings */}
                <div className="flex justify-between items-center p-3 flex-shrink-0 bg-white border-b border-gray-200">
                    <div className="flex items-center gap-2 min-w-0 flex-1">

                        {/* Logo */}
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg shadow-sm flex-shrink-0">
                            <img
                                src="/logo.webp"
                                alt="Neurolaw AI Logo"
                                className="w-full h-full object-contain"
                                style={{ mixBlendMode: "multiply" }}
                            />
                        </div>
                        <h1 className="text-base font-bold text-black truncate">Neurolaw AI</h1>
                    </div>

                    {/* Close Button */}
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="p-2 text-gray-500 hover:text-black hover:bg-gray-100 rounded-lg transition-colors active:bg-gray-200 flex-shrink-0"
                        aria-label="Close menu"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Navigation - Scrollable */}
                <div className="flex-1 overflow-y-auto min-h-0 py-2 px-2 space-y-0.5">
                    {orderedNavItems.map((navItem) => {
                        if (navItem.type === 'link') {
                            return (
                                <MobileNavLink
                                    key={navItem.item.id}
                                    item={navItem.item}
                                    location={location}
                                    onNavigate={onNavigate}
                                    setSidebarOpen={setSidebarOpen}
                                />
                            );
                        } else if (navItem.type === 'dropdown') {
                            return (
                                <AccordionItem
                                    key={navItem.menu.id}
                                    menu={navItem.menu}
                                    location={location}
                                    setSidebarOpen={setSidebarOpen}
                                />
                            );
                        }
                        return null;
                    })}
                </div>

                {/* Bottom Section - User Profile with Popup */}
                <div className="border-t border-gray-200 p-3 flex-shrink-0 bg-white">
                    {/* Credits & Points Display */}
                    {/* <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center gap-2 flex-1 bg-green-50 rounded-lg px-3 py-2">
                            <Banknote size={16} className="text-green-600 flex-shrink-0" />
                            <span className="text-xs font-medium text-green-700 truncate">{Math.floor(reduxCredits).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-1 bg-amber-50 rounded-lg px-3 py-2">
                            <img src="/n-coin.webp" alt="Coins" className="w-4 h-4 flex-shrink-0" />
                            <span className="text-xs font-medium text-amber-600 truncate">{Math.floor(reduxPoints).toLocaleString()}</span>
                        </div>
                    </div> */}

                    {/* Plan Title */}
                    {user?.subscriptionType === 'plan' && user?.expiryTime && (
                        <div className="mb-3 px-2 py-2 bg-gray-100 rounded-lg">
                            <p className="text-xs font-semibold text-gray-900 mb-0.5">Paid Plan</p>
                            <p className="text-xs text-gray-500">Until {dayjs(user.expiryTime).format('MMM DD, YYYY')}</p>
                        </div>
                    )}
                    
                    <div className="relative">
                        {/* User Profile Button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsProfilePopupOpen(!isProfilePopupOpen);
                            }}
                            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                        >
                            <div className={clsx(
                                "w-8 h-8 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0",
                                user?.subscriptionType === 'plan' && "ring-2 ring-yellow-500 ring-offset-2",
                                isOrgMember && "ring-2 ring-purple-500 ring-offset-2"
                            )}>
                                {user?.profilePicture_url ? (
                                    <img src={user.profilePicture_url} alt={getUserDisplayName(user).name} className="w-full h-full rounded-full object-cover" />
                                ) : (
                                    getInitials(user)
                                )}
                            </div>
                            <div className="min-w-0 flex-1 text-left">
                                <div className="flex items-center gap-1">
                                    <p className="text-sm font-medium text-black truncate">{getUserDisplayName(user).name || 'User'}</p>
                                    {getUserDisplayName(user).isOrganization && (
                                        <Building2 size={12} className="text-gray-500 flex-shrink-0" />
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
                            </div>
                        </button>

                        {/* Profile Popup Menu */}
                        {isProfilePopupOpen && (
                            <>
                                {/* Backdrop to close on click outside */}
                                <div
                                    className="fixed inset-0 z-[9998]"
                                    onClick={() => setIsProfilePopupOpen(false)}
                                />

                                {/* Popup */}
                                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-[9999]">
                                    {/* User Info */}
                                    <div
                                        className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            actions.Profile();
                                            setIsProfilePopupOpen(false);
                                            setSidebarOpen(false);
                                        }}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={clsx(
                                                "w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0",
                                                user?.subscriptionType === 'plan' && "ring-2 ring-yellow-500 ring-offset-2"
                                            )}>
                                                {user?.profilePicture_url ? (
                                                    <img src={user.profilePicture_url} alt={getUserDisplayName(user).name} className="w-full h-full rounded-full object-cover" />
                                                ) : (
                                                    getInitials(user)
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1 text-left">
                                                <div className="flex items-center gap-1">
                                                    <p className="text-sm font-semibold text-gray-900 truncate">{getUserDisplayName(user).name || 'User'}</p>
                                                    {getUserDisplayName(user).isOrganization && (
                                                        <Building2 size={12} className="text-gray-500 flex-shrink-0" />
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Menu Items */}
                                    <div className="py-1">
                                        {/* AI Assistant Toggle */}
                                        <div className="px-4 py-3 hover:bg-gray-50 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <Sparkles size={18} className="text-gray-600 flex-shrink-0" />
                                                <span className="text-sm text-gray-700 flex-1">AI Assistant</span>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowAssistant(prev => !prev);
                                                        localStorage.setItem('showAssistant', JSON.stringify(!showAssistant));
                                                        window.dispatchEvent(new CustomEvent('toggleAssistant'));
                                                    }}
                                                    className={clsx(
                                                        "w-10 h-5 rounded-full transition-all duration-300 p-0.5 flex-shrink-0",
                                                        showAssistant ? "bg-gray-900" : "bg-gray-300"
                                                    )}
                                                >
                                                    <div className={clsx(
                                                        "w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-300",
                                                        showAssistant ? "translate-x-5" : "translate-x-0"
                                                    )} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Support */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                /* Support modal logic would go here */
                                                setIsProfilePopupOpen(false);
                                            }}
                                            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                                        >
                                            <MessageCircle size={18} className="text-gray-600 flex-shrink-0" />
                                            <span className="text-sm text-gray-700">Support</span>
                                        </button>

                                        {/* Settings */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(ROUTES.SETTINGS);
                                                setIsProfilePopupOpen(false);
                                                setSidebarOpen(false);
                                            }}
                                            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                                        >
                                            <Settings size={18} className="text-gray-600 flex-shrink-0" />
                                            <span className="text-sm text-gray-700">Settings</span>
                                        </button>

                                        {/* Logout */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                actions.logout();
                                                setIsProfilePopupOpen(false);
                                                setSidebarOpen(false);
                                            }}
                                            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-red-50 transition-colors text-left border-t border-gray-200"
                                        >
                                            <LogOut size={18} className="text-red-500 flex-shrink-0" />
                                            <span className="text-sm text-red-500">Logout</span>
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </aside>
        </div>
    );
};

export default MobileNav;